import InputField from "components/fields/InputField";
import { FcGoogle } from "react-icons/fc";
import Checkbox from "components/checkbox";
import { useState, useEffect } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";
import jwt_encode from 'jwt-encode';
import { jwtDecode } from 'jwt-decode';
import { sessionManager } from 'utils/sessionManager';
import { tokenManager } from 'utils/tokenManager';
import { accessControl } from 'utils/accessControl';

export default function SignIn() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  
  // Get selected department from localStorage
  const selectedDepartment = localStorage.getItem('selectedDepartment') || 'USIBRAS';

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear error when user types
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username or Email is required";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post('https://usibras-web-backend.onrender.com/api/v1/auth/login', formData);
      
      if (response.data.message === "Login successful") {
        // Create a secret key for JWT encoding (in production, this should be an environment variable)
        const secret = 'your-secret-key';
        
        // Encrypt user data using JWT
        const encodedUserData = jwt_encode(response.data.user, secret);
        
        // Store the encrypted data with session management
        sessionManager.storeLoginData(encodedUserData);
        
        // Store encrypted authentication token using token manager
        tokenManager.storeToken(
          response.data.token, 
          response.data.token_type || 'Bearer', 
          response.data.expires_in || 43200
        );
        
        // Refresh access control
        accessControl.refreshPermissions();
        toast.success('Login successful!');
        
        // Add a delay to ensure localStorage operations complete before navigation
        // This prevents race conditions where components try to read tokens before they're fully stored
        setTimeout(() => {
          navigate(accessControl.getLandingPage());
        }, 250);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Check session validity on component mount
  useEffect(() => {
    if (sessionManager.isSessionValid()) {
      // If session is valid, redirect to appropriate landing page
      navigate(accessControl.getLandingPage());
    }
  }, [navigate]);

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-start justify-start px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-start lg:justify-start">
      <Toaster position="top-right" />
      {/* Sign in section */}
      <div className="mt-[10vh] w-full max-w-full flex-col items-start md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Sign in to <span className="text-brand-500">{selectedDepartment}</span> Department
        </h4>
        <p className="mb-9 text-base text-gray-600">
          Enter your email and password to sign in!
        </p>
        {/* Commented out Google Sign In
        <div className="mb-6 flex h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-lightPrimary hover:cursor-pointer dark:bg-navy-800">
          <div className="rounded-full text-xl">
            <FcGoogle />
          </div>
          <h5 className="text-sm font-medium text-navy-700 dark:text-white">
            Sign In with Google
          </h5>
        </div>
        */}

        {/* Commented out Divider
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
          <p className="text-base text-gray-600 dark:text-white"> or </p>
          <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
        </div>
        */}

        <form onSubmit={handleSubmit}>
          {/* Username/Email field */}
          <InputField
            variant="auth"
            extra="mb-3"
            label="Username or Email*"
            placeholder="Enter your username or email"
            id="username"
            type="text"
            value={formData.username}
            onChange={handleChange}
            error={errors.username}
            required
          />

          {/* Password with toggle */}
          <div className="relative">
            <InputField
              variant="auth"
              extra="mb-3"
              label="Password*"
              placeholder="Min. 8 characters"
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[50px] transform -translate-y-1/4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showPassword ? (
                <MdVisibilityOff className="h-5 w-5" />
              ) : (
                <MdVisibility className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Checkbox */}
          <div className="mb-4 flex items-center justify-between px-2">
            <div className="flex items-center">
              <Checkbox />
              <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                Keep me logged In
              </p>
            </div>
            <a
              className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
              href=" "
            >
              Forgot Password?
            </a>
          </div>
          <button 
            type="submit" 
            disabled={isLoading}
            className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {isLoading ? (
              <ClipLoader size={20} color="#ffffff" />
            ) : (
              'Sign In'
            )}
          </button>
        </form>
        {/* <div className="mt-4">
          <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
            Not registered yet?
          </span>
          <a
            href=" "
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Create an account
          </a>
        </div> */}
      </div>
    </div>
  );
}
